import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { apiProducts } from '../../api/apiProducts.api';
import DotSpinner from '../../components/shared/DotSpinner';
import { apiPayment } from '../../api/apiPayment.api';
import { toast } from 'react-toastify';
import { openLink } from '../../utils/openLink';
import { useSubscriptionTerms } from '../../components/terms/SubscriptionTerms';
import config from '../../config';

function calcWeeklyPrice(price, days) {
  const weeks = days / 7;
  if (weeks === 1) return price;

  return (price / weeks).toFixed(2);
}

const CURRENCY_CONFIG = {
  BYN: {
    Icon: ({ className = '' }) => (
      <img src="/icons/BYN-white.svg" alt="currency" className={`ml-[4px] w-[12px] ${className}`} />
    ),
    IconSmall: ({ className = '' }) => (
      <img src="/icons/BYN-small.svg" alt="currency" className={`w-[7px] mx-1 ${className}`} />
    )
  },
  STAR: {
    Icon: ({ className = '' }) => <img src="/icons/tg_star.png" className={`ml-[4px] w-[16px] ${className}`} />,
    IconSmall: ({ className = '' }) => (
      <img src="/icons/tg_star.svg" alt="currency" className={`w-[13px] mx-1 ${className}`} />
    )
  }
};

const TITLE_SUBSCRIBE = {
  premium_weekly: 'Неделя',
  premium_monthly: 'Месяц',
  premium_quarterly: '3 месяца'
};

export function Subscription() {
  const { CheckBox, isChecked, onAccept } = useSubscriptionTerms();

  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [isLoading, setIsLoading] = useState(false);
  const disabledButton = isLoading || !isChecked;

  const [payment, setPayment] = useState('STAR');
  const [products, setProducts] = useState([]);
  const [productSelected, setProductSelected] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    apiProducts
      .fetchAllProducts(axiosPrivate, { type: 'subscription' })
      .then((res) => {
        setProducts(res.data.items);
        setProductSelected(res.data.items[1] || null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleByProduct = async () => {
    if (config.PURCHASES_FROZEN) {
      toast.info(config.PURCHASES_FROZEN_MESSAGE);
      return;
    }
    if (!productSelected) return;
    try {
      await onAccept();

      const response =
        payment === 'BYN'
          ? await apiPayment.createOrder(axiosPrivate, productSelected._id)
          : await apiPayment.createInvoice(axiosPrivate, productSelected._id);

      if (payment === 'BYN') {
        openLink(response.redirectUrl);
      } else {
        toast.success('Оплатите заказ в боте!');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  if (isLoading || !productSelected) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="relative h-full bg-black">
      {/* Фон */}
      <div className="absolute top-0 left-0 right-0">
        <img src="/icons/premium_people.svg" className="w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, black 0%, transparent 100%)' }}
        />
      </div>

      {/* Контент */}
      <div className="relative z-10 h-full px-4 overflow-scroll">
        <button className="absolute top-[21px] right-[16px] p-4 rounded-full bg-white/10" onClick={() => navigate(-1)}>
          <img src="/icons/close.svg" alt="close" />
        </button>

        <div className="pt-28 mb-3">
          <p className="font-raleway text-[40px] font-bold text-white text-center">MYTA PREMIUM</p>
          <p className="font-raleway text-[16px] font-medium text-white text-center w-full">
            C Premium вы найдёте партнёра в два раза быстрее
          </p>
        </div>

        {/* Переключение типа оплаты */}
        <div className="flex items-center justify-between bg-[#022424] px-1.5 py-1 rounded-[400px] font-raleway mb-7 border-2 border-[#233636]">
          <div
            onClick={() => {
              config.BYN_PAYMENT_FROZEN ? toast.info(config.PURCHASES_FROZEN_MESSAGE) : setPayment('BYN');
            }}
            className={` ${payment === 'BYN' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center `}
          >
            <span className={`${payment === 'BYN' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>Деньги</span>
          </div>
          <div
            onClick={() => {
              setPayment('STAR');
            }}
            className={` ${payment === 'STAR' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center`}
          >
            <span className={`${payment === 'STAR' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>
              Telegram звезды
            </span>
          </div>
        </div>

        {/* Карточки подписок */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {products.map((p, index) => (
            <SubscriptionCard
              key={p._id}
              payment={payment}
              title={TITLE_SUBSCRIBE[p.slug] || p.name}
              prices={p.prices}
              bonus={p.bonus.amount}
              baseAmount={p.baseAmount}
              baseCurrency={p.baseCurrency}
              isPopular={index === 1}
              isSelected={productSelected?._id === p._id}
              onClick={() => {
                setProductSelected(p);
              }}
            />
          ))}
        </div>

        <div className="w-full px-4">
          <table className="w-full text-center text-white font-raleway border-separate border-spacing-0">
            <tr>
              <th className="w-[50%]"></th>
              <th className="w-[20%]">FREE</th>
              <th className="w-[30%] text-[#A1F69E]">PREMIUM</th>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">Монеты MYTA</td>
              <td className="border-b border-r border-[#233636] h-[35px]">—</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">
                {productSelected?.bonus.amount || 0}
                <img src="/icons/myta-coin.svg" className="ml-[7px] w-[20px] h-[20px]" />
              </td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">
                Лайк анкеты <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[35px]">70</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">∞</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left">Узнать кто вас лайкнул</td>
              <td className="border-b border-r border-[#233636] h-full">—</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">∞</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">
                Супер-лайк <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[35px]">1</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">5</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left h-[55px]">
                Вернуть анкету
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[55px]">1</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">5</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left h-[55px]">
                Видео-знакомства
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[55px]">40</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">250</td>
            </tr>
            <tr className="h-[62px]">
              <td className="border-b border-r border-[#233636] h-[62px] text-left">
                Лайк в видео-знакомствах
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[62px]">4</td>
              <td className="border-b border-[#233636] h-full">15</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">Реклама</td>
              <td className="border-b border-r border-[#233636] h-[35px]">есть</td>
              <td className="border-b border-[#233636] h-[35px]r">—</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-r border-[#233636] text-left h-[35px]">
                Бонус <span>(подарки)</span>
              </td>
              <td className="border-r border-[#233636] h-[35px]">—</td>
              <td className="border-[#233636] flex flex-row items-center justify-center h-[35px]">4 🎁</td>
            </tr>
          </table>
        </div>

        <p className="mt-[20px] text-center font-normal left-1.4 text-[12px] text-white opacity-70 pb-[220px]">
          Подписка будет продлеваться автоматически, если она не отменена как минимум за 24 часа до окончания текущего
          периода. Подписку можно отменить в любой момент
        </p>

        <div className="fixed bg-black bottom-0 left-0 right-0 px-4 pb-6">
          <Button
            onClick={handleByProduct}
            className={`w-full ${config.PURCHASES_FROZEN ? 'opacity-30 cursor-not-allowed' : ''}`}
            disabled={!config.PURCHASES_FROZEN && disabledButton}
          >
            {isLoading ? 'Загрузка...' : 'Подключить Premium'}
          </Button>
          <CheckBox />
        </div>
      </div>
    </div>
  );
}

export function SubscriptionCard({
  payment,
  title,
  prices,
  bonus,
  onClick,
  baseAmount,
  baseCurrency,
  isPopular,
  isSelected
}) {
  const { Icon, IconSmall } = CURRENCY_CONFIG[payment] || CURRENCY_CONFIG.BYN;
  const currentPricesInfo = prices.find((p) => p.currency === payment);

  return (
    <div
      onClick={onClick}
      className={`relative flex flex-col items-center justify-start w-full  text-white border ${
        isSelected ? 'border-[#A1F69E] bg-[#043939]' : 'border-[#233636] bg-[#022424]'
      } rounded-[16px] cursor-pointer`}
    >
      {isPopular && (
        <div className="absolute min-w-[122px] w-full -top-[20px] left-1/2 -translate-x-1/2 bg-[#A1F69E] rounded-full text-black text-sm text-center font-bold font-sans px-2 py-1">
          ПОПУЛЯРНЫЙ
        </div>
      )}
      <span
        className={`border-b pt-[10px] pb-[5px] w-full text-center mb-[6px] ${
          isSelected ? 'border-[#0F7474]' : 'border-[#233636]'
        } text-[16px]`}
      >
        {title}
      </span>

      <span className="flex-col flex items-center justify-center">
        <div className="flex items-center justify-center">
          <p className="text-[22px] font-semibold">{calcWeeklyPrice(currentPricesInfo.amount, baseAmount)}</p>
          <Icon className="w-[20px] h-[20px]" />
        </div>
        <p className="text-[14px] font-normal ">в неделю</p>
      </span>

      {!currentPricesInfo.oldAmount && (
        <span className="text-[14px] mb-1 opacity-50 flex items-center justify-center">
          {currentPricesInfo.amount} <IconSmall />
        </span>
      )}

      {currentPricesInfo.oldAmount && (
        <span className="relative text-white text-[16px] opacity-80 text-center">
          <div className="ml-[5px] flex flex-row items-center justify-center">
            {currentPricesInfo.oldAmount} <IconSmall />
          </div>
          <span className="absolute inset-0 w-full h-[2px] bg-red-500 rotate-[12deg] top-1/2 left-0"></span>
        </span>
      )}

      <div className="flex flex-row justify-center items-center gap-[2px] mt-auto mb-2">
        <span className="text-[17.21px] font-raleway">+{bonus}</span>
        <img src="/icons/myta-coin.svg" className="w-[16.9px] h-[16.9px]" />
      </div>
    </div>
  );
}
